import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type JobBody =
  paths["/api/v1/mailing/jobs"]["post"]["requestBody"]["content"]["application/json"];

export const mailingKeys = {
  jobs: ["mailing", "jobs"] as const,
  templates: ["mailing", "templates"] as const,
};

type JobUpdateBody =
  paths["/api/v1/mailing/jobs/{job_id}"]["patch"]["requestBody"]["content"]["application/json"];

export function useUpdateMailingJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: JobUpdateBody }) => {
      const { data, error } = await apiClient.PATCH("/api/v1/mailing/jobs/{job_id}", {
        params: { path: { job_id: id } },
        body,
      });
      if (error || !data) throw new Error("Could not update mailing");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mailingKeys.jobs }),
  });
}

export function usePreviewMailingJob() {
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/mailing/jobs/{job_id}/preview",
        { params: { path: { job_id: id } } },
      );
      if (error || !data) throw new Error("Could not render preview");
      return data;
    },
  });
}

export function useMailingTemplates() {
  return useQuery({
    queryKey: mailingKeys.templates,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/mailing/templates", {});
      if (error || !data) throw new Error("Failed to load templates");
      return data;
    },
  });
}

export function useMailingJobs() {
  return useQuery({
    queryKey: mailingKeys.jobs,
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/api/v1/mailing/jobs", {});
      if (error || !data) throw new Error("Failed to load mailing jobs");
      return data;
    },
  });
}

export function useCreateMailingJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: JobBody) => {
      const { data, error } = await apiClient.POST("/api/v1/mailing/jobs", { body });
      if (error || !data) throw new Error("Failed to create mailing job");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mailingKeys.jobs }),
  });
}

export function useRunMailingJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/mailing/jobs/{job_id}/run",
        { params: { path: { job_id: jobId } } },
      );
      if (error || !data) throw new Error("Failed to run mailing job");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mailingKeys.jobs }),
  });
}
